import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Download, FileText } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { emettiDdtSpedizione, scaricaDdtSpedizione } from '@/api/ordini'

function localDateTime(value) {
  const date = value ? new Date(value) : new Date()
  const pad = (number) => String(number).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

export default function DdtButton({ spedizione, variant = 'default', size = 'default' }) {
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [dataSpedizione, setDataSpedizione] = useState('')
  const queryClient = useQueryClient()
  const issued = Boolean(spedizione.ddt_numero)

  const handleClick = () => {
    if (!issued) {
      setDataSpedizione(localDateTime(spedizione.data_spedizione))
      setOpen(true)
      return
    }
    download()
  }

  const download = async (confirmedDate) => {
    setLoading(true)
    try {
      const response = issued
        ? await scaricaDdtSpedizione(spedizione.id)
        : await emettiDdtSpedizione(spedizione.id, confirmedDate)
      const url = window.URL.createObjectURL(response.data)
      const link = document.createElement('a')
      link.href = url
      link.download = `DDT_${spedizione.codice || spedizione.id}.pdf`
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['spedizione'] }),
        queryClient.invalidateQueries({ queryKey: ['spedizioni'] }),
        queryClient.invalidateQueries({ queryKey: ['ordine'] }),
      ])
      setOpen(false)
      toast.success('DDT scaricato')
    } catch (error) {
      let message = 'Impossibile generare il DDT.'
      if (error.response?.data instanceof Blob) {
        try {
          const body = JSON.parse(await error.response.data.text())
          message = Array.isArray(body.error) ? body.error.join(' ') : body.error || message
        } catch { /* Risposta non JSON. */ }
      }
      toast.error(message)
    } finally {
      setLoading(false)
    }
  }

  const handleIssue = () => {
    const date = new Date(dataSpedizione)
    if (!dataSpedizione || Number.isNaN(date.getTime())) {
      toast.error('Indica una data e un’ora di spedizione valide.')
      return
    }
    download(date.toISOString())
  }

  return (
    <>
      <Button
        type="button"
        variant={variant}
        size={size}
        onClick={handleClick}
        disabled={loading || (!issued && !spedizione.numero_colli)}
        title={!issued && !spedizione.numero_colli ? 'Assegna prima la merce ai colli' : undefined}
      >
        {issued ? <Download className="mr-2 h-4 w-4" /> : <FileText className="mr-2 h-4 w-4" />}
        {loading ? 'Preparazione...' : issued ? `Scarica DDT ${spedizione.ddt_numero}` : 'Genera DDT'}
      </Button>
      {!issued && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Genera DDT</DialogTitle>
              <DialogDescription>
                Conferma quando è partita la merce. Questa data sarà salvata nella spedizione e stampata sul DDT; il documento riceverà un numero definitivo.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label htmlFor={`ddt-data-spedizione-${spedizione.id}`}>Data e ora di spedizione</Label>
              <Input
                id={`ddt-data-spedizione-${spedizione.id}`}
                type="datetime-local"
                value={dataSpedizione}
                onChange={(event) => setDataSpedizione(event.target.value)}
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={loading}>Annulla</Button>
              <Button type="button" onClick={handleIssue} disabled={loading || !dataSpedizione}>
                {loading ? 'Emissione...' : 'Conferma ed emetti DDT'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}
